import React, { FC, PropsWithChildren } from "react";
import { Link } from "../link";
import type { HeaderTranslations } from "@/translations";
import { useRouter } from "next/router";

interface Props {
  data: HeaderTranslations;
  showOnMobile: boolean;
}

interface SubListState {
  showSubList: boolean;
}

export const HeaderNav: FC<PropsWithChildren<Props>> = ({
  data,
  showOnMobile,
}) => {
  const { asPath } = useRouter();
  const [subListStates, setSubListStates] = React.useState<SubListState[]>([]);

  const handleSubListMouseEnter = (index: number) => {
    if (window.innerWidth < 1024) return;

    const updatedSubListStates = [...subListStates];
    const subListState = updatedSubListStates[index];

    subListState.showSubList = true;

    setSubListStates(updatedSubListStates);
  };

  const handleSubListMouseLeave = (index: number) => {
    if (window.innerWidth < 1024) return;

    const updatedSubListStates = [...subListStates];
    const subListState = updatedSubListStates[index];

    subListState.showSubList = false;

    setSubListStates(updatedSubListStates);
  };

  const isActiveLink = (href: string) => {
    return asPath === href;
  };

  React.useEffect(() => {
    const initialSubListStates = data.aboutUs.children.map(() => ({
      showSubList: window.innerWidth >= 1024 ? false : true,
      timeoutId: undefined,
    }));
    setSubListStates(initialSubListStates);
  }, [data.aboutUs.children]);

  return (
    <nav className={`header--nav ${showOnMobile ? "header--nav_visible" : ""}`}>
      <ul className="header--nav-list">
        <li className="header--nav-list-item" data-title={data.platform.text}>
          <Link
            noStyle
            href={data.platform.link?.uri as string}
            className={`header--nav-list-link ${
              isActiveLink(data.platform.link?.uri as string)
                ? "header--nav-list-link_active"
                : ""
            }`}
          >
            {data.platform.text}
          </Link>
        </li>
        <li className="header--nav-list-item" data-title={data.solutions.text}>
          <Link
            href={data.solutions.link?.uri as string}
            noStyle
            className={`header--nav-list-toggler ${
              subListStates[0]?.showSubList
                ? "header--nav-list-toggler_active"
                : ""
            }`}
            onMouseEnter={() => handleSubListMouseEnter(0)}
            onMouseLeave={() => handleSubListMouseLeave(0)}
          >
            <span>{data.solutions.text}</span>
          </Link>
          {data.solutions.children.length > 0 && (
            <ul
              className={`header--nav-subList ${
                subListStates[0]?.showSubList
                  ? "header--nav-subList_visible"
                  : ""
              }`}
              onMouseEnter={() => handleSubListMouseEnter(0)}
              onMouseLeave={() => handleSubListMouseLeave(0)}
            >
              {data.solutions.children.map((item, index) => {
                return (
                  <li key={index}>
                    <Link
                      noStyle
                      href={item.link?.uri as string}
                      className={`header--nav-subList-link ${
                        isActiveLink(item.link?.uri as string)
                          ? "header--nav-subList-link_active"
                          : ""
                      }`}
                    >
                      <span>{item.text}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
        <li className="header--nav-list-item" data-title={data.ecosystem.text}>
          <Link
            href={data.ecosystem.link?.uri as string}
            noStyle
            className={`header--nav-list-toggler ${
              subListStates[1]?.showSubList
                ? "header--nav-list-toggler_active"
                : ""
            }`}
            onMouseEnter={() => handleSubListMouseEnter(1)}
            onMouseLeave={() => handleSubListMouseLeave(1)}
          >
            <span>{data.ecosystem.text}</span>
          </Link>
          {data.ecosystem.children.length > 0 && (
            <ul
              className={`header--nav-subList ${
                subListStates[1]?.showSubList
                  ? "header--nav-subList_visible"
                  : ""
              }`}
              onMouseEnter={() => handleSubListMouseEnter(1)}
              onMouseLeave={() => handleSubListMouseLeave(1)}
            >
              {data.ecosystem.children.map((item, index) => {
                return (
                  <li key={index}>
                    <Link
                      noStyle
                      href={item.link?.uri as string}
                      className={`header--nav-subList-link ${
                        isActiveLink(item.link?.uri as string)
                          ? "header--nav-subList-link_active"
                          : ""
                      }`}
                    >
                      <span>{item.text}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
        <li className="header--nav-list-item" data-title={data.resources.text}>
          <Link
            href={data.resources.link?.uri as string}
            noStyle
            className={`header--nav-list-toggler ${
              subListStates[2]?.showSubList
                ? "header--nav-list-toggler_active"
                : ""
            }`}
            onMouseEnter={() => handleSubListMouseEnter(2)}
            onMouseLeave={() => handleSubListMouseLeave(2)}
          >
            <span>{data.resources.text}</span>
          </Link>
          {data.resources.children.length > 0 && (
            <ul
              className={`header--nav-subList ${
                subListStates[2]?.showSubList
                  ? "header--nav-subList_visible"
                  : ""
              }`}
              onMouseEnter={() => handleSubListMouseEnter(2)}
              onMouseLeave={() => handleSubListMouseLeave(2)}
            >
              {data.resources.children.map((item, index) => {
                return (
                  <li key={index}>
                    <Link
                      noStyle
                      href={item.link?.uri as string}
                      className={`header--nav-subList-link ${
                        isActiveLink(item.link?.uri as string)
                          ? "header--nav-subList-link_active"
                          : ""
                      }`}
                    >
                      <span>{item.text}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
        <li className="header--nav-list-item" data-title={data.aboutUs.text}>
          <button
            className={`header--nav-list-toggler ${
              subListStates[3]?.showSubList
                ? "header--nav-list-toggler_active"
                : ""
            }`}
            onMouseEnter={() => handleSubListMouseEnter(3)}
            onMouseLeave={() => handleSubListMouseLeave(3)}
          >
            <span>{data.aboutUs.text}</span>
          </button>
          {data.aboutUs.children.length > 0 && (
            <ul
              className={`header--nav-subList ${
                subListStates[3]?.showSubList
                  ? "header--nav-subList_visible"
                  : ""
              }`}
              onMouseEnter={() => handleSubListMouseEnter(3)}
              onMouseLeave={() => handleSubListMouseLeave(3)}
            >
              {data.aboutUs.children.map((item, index) => {
                return (
                  <li key={index}>
                    <Link
                      noStyle
                      href={item.link?.uri as string}
                      className={`header--nav-subList-link ${
                        isActiveLink(item.link?.uri as string)
                          ? "header--nav-subList-link_active"
                          : ""
                      }`}
                    >
                      <span>{item.text}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      </ul>
      {data.getInTouch.link?.uri && (
        <div className="header--nav-cta-wrapper">
          <Link
            href={data.getInTouch.link?.uri}
            className="header--nav-cta"
            title={data.getInTouch.text}
          >
            <span>{data.getInTouch.text}</span>
          </Link>
        </div>
      )}
    </nav>
  );
};
